import { useCallback, useEffect, useRef, useState } from 'react'

import {
  deleteStoredTalentPoolCV,
  getStoredTalentPoolCV,
  storeTalentPoolCV,
} from '@/lib/indexed-db'
import {
  fileHasPdfSignature,
  isAcceptedClientPdfFile,
} from '@/lib/files/pdf'
import { createClient } from '@/lib/supabase/client'
import {
  OAUTH_NEXT_COOKIE_MAX_AGE_SECONDS,
  OAUTH_NEXT_COOKIE_NAME,
} from '@/lib/auth/oauth-constants'

import { talentPoolCopy } from '@/app/talent-pool/copy'
import { pushTalentPoolGtmEvent } from './talent-pool-analytics'
import {
  TALENT_POOL_STEP_ORDER,
  type TalentPoolStep,
} from './talent-pool-page-types'
import { composeExpectedSalaryRange, formatIdrAmount } from '@/app/talent-pool/salary-range'
import type { TalentPoolFormDraft } from '@/app/talent-pool/talent-pool-store'
import {
  getPersonalStepErrors,
  getPreferencesStepErrors,
} from './talent-pool-validation'

const TALENT_POOL_RESUME_PATH = '/talent-pool?resume=true&auto-submit=true'

type UseTalentPoolSubmissionStateOptions = {
  copy: typeof talentPoolCopy
  draft: TalentPoolFormDraft
  file: File | null
  storedFileName: string | null
  setFile: (file: File | null) => void
  setStoredFileName: (name: string | null) => void
  markSubmitted: (cvId: string) => void
  hasHydrated: boolean
  hasPersistedSubmission: boolean
  isResume: boolean
  shouldAutoSubmit: boolean
  oauthStatus: string | null
  oauthReason: string | null
  setSubmitError: (message: string) => void
  routerPush: (href: string) => void
}

export const useTalentPoolSubmissionState = ({
  copy,
  draft,
  file,
  storedFileName,
  setFile,
  setStoredFileName,
  markSubmitted,
  hasHydrated,
  hasPersistedSubmission,
  isResume,
  shouldAutoSubmit,
  oauthStatus,
  oauthReason,
  setSubmitError,
  routerPush,
}: UseTalentPoolSubmissionStateOptions) => {
  const [step, setStep] = useState<TalentPoolStep>('landing')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const autoSubmitAttemptedRef = useRef(false)
  const autoSubmitInFlightRef = useRef(false)
  const submitInFlightRef = useRef(false)
  const oauthRetryAttemptedRef = useRef(false)

  const loginSyncErrorMessage =
    'Sesi login belum tersinkron. Muat ulang halaman ini, lalu coba kirim lagi.'

  useEffect(() => {
    if (!isResume || !hasHydrated) return
    const restore = async () => {
      const stored = await getStoredTalentPoolCV()
      if (!stored?.file) return

      if (
        !isAcceptedClientPdfFile(stored.file) ||
        !(await fileHasPdfSignature(stored.file))
      ) {
        setFile(null)
        setStoredFileName(null)
        await deleteStoredTalentPoolCV()
        setSubmitError(copy.validation.fileType)
        return
      }

      setFile(stored.file)
      setStoredFileName(stored.file.name)
      setStep('submit')
    }
    void restore()
  }, [
    copy.validation.fileType,
    hasHydrated,
    isResume,
    setFile,
    setStoredFileName,
    setSubmitError,
  ])

  const validatePersonalStep = useCallback(() => {
    const errors = getPersonalStepErrors(draft, copy.validation)
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }, [copy.validation, draft])

  const validatePreferencesStep = useCallback(() => {
    const errors = getPreferencesStepErrors(draft, copy.validation)
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }, [copy.validation, draft])

  const goNext = useCallback(() => {
    if (step === 'personal' && !validatePersonalStep()) return
    if (step === 'preferences' && !validatePreferencesStep()) return
    const index = TALENT_POOL_STEP_ORDER.indexOf(step)
    if (index >= TALENT_POOL_STEP_ORDER.length - 1) return

    const nextStep = TALENT_POOL_STEP_ORDER[index + 1]!
    setStep(nextStep)
    setFieldErrors({})
    window.scrollTo({ top: 0, behavior: 'smooth' })
    pushTalentPoolGtmEvent('talent_pool_step_advance', { from: step, to: nextStep })
  }, [step, validatePersonalStep, validatePreferencesStep])

  const goBack = useCallback(() => {
    const index = TALENT_POOL_STEP_ORDER.indexOf(step)
    if (index <= 0) return
    setStep(TALENT_POOL_STEP_ORDER[index - 1]!)
    setFieldErrors({})
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [step])

  const handleLandingNext = useCallback(() => {
    pushTalentPoolGtmEvent('talent_pool_form_landing_next_click', {
      page: 'talent-pool',
      placement: 'form',
      step: 'landing',
      has_file: Boolean(file || storedFileName),
    })
    goNext()
  }, [file, goNext, storedFileName])

  const handleGoogleLogin = useCallback(async () => {
    setIsAuthenticating(true)
    try {
      if (file) await storeTalentPoolCV({ file })
      pushTalentPoolGtmEvent('talent_pool_oauth_google_start', {
        page: 'talent-pool',
        placement: 'submit',
        step,
      })
      const supabase = createClient()
      document.cookie = `${OAUTH_NEXT_COOKIE_NAME}=${encodeURIComponent(TALENT_POOL_RESUME_PATH)}; path=/; max-age=${OAUTH_NEXT_COOKIE_MAX_AGE_SECONDS}; samesite=lax`
      const redirectTo = `${window.location.origin}/auth/oauth?next=${encodeURIComponent(TALENT_POOL_RESUME_PATH)}`
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo },
      })
      if (!error) return

      pushTalentPoolGtmEvent('talent_pool_oauth_google_client_error', {
        page: 'talent-pool',
        placement: 'submit',
        error_message: error.message,
      })
      setSubmitError(error.message)
      setIsAuthenticating(false)
    } catch {
      pushTalentPoolGtmEvent('talent_pool_oauth_google_client_exception', {
        page: 'talent-pool',
        placement: 'submit',
      })
      setSubmitError('Login gagal. Silakan coba lagi.')
      setIsAuthenticating(false)
    }
  }, [file, setSubmitError, step])

  const handleSubmit = useCallback(async () => {
    if (submitInFlightRef.current) return
    submitInFlightRef.current = true

    if (!validatePersonalStep() || !validatePreferencesStep()) {
      setSubmitError(copy.validation.required)
      submitInFlightRef.current = false
      return
    }

    setIsSubmitting(true)
    setSubmitError('')
    try {
      let submitFile = file
      if (!submitFile) {
        const stored = await getStoredTalentPoolCV()
        submitFile = stored?.file ?? null
      }
      if (!submitFile) {
        setSubmitError(copy.validation.fileRequired)
        setIsSubmitting(false)
        return
      }

      if (
        !isAcceptedClientPdfFile(submitFile) ||
        !(await fileHasPdfSignature(submitFile))
      ) {
        setFile(null)
        setStoredFileName(null)
        await deleteStoredTalentPoolCV()
        setSubmitError(copy.validation.fileType)
        setIsSubmitting(false)
        return
      }

      const expectedSalaryRange =
        composeExpectedSalaryRange(draft.expectedSalaryMin, draft.expectedSalaryMax) ||
        draft.expectedSalaryRange.trim() ||
        (draft.expectedSalaryMin.trim() ? `Rp ${formatIdrAmount(draft.expectedSalaryMin)}` : '')

      const preferences = {
        fullName: draft.fullName.trim(),
        emailAddress: draft.emailAddress.trim(),
        phoneNumber: draft.phoneNumber.trim(),
        birthDate: draft.birthDate,
        targetedRoles: draft.targetedRoles,
        expectedSalaryRange,
        currentCity: draft.currentCity.trim(),
        willingToRelocate: draft.willingToRelocate,
        status: draft.status,
      }

      const formData = new FormData()
      formData.append('file', submitFile)
      formData.append('preferences', JSON.stringify(preferences))
      const response = await fetch('/api/talent-pool/submit', { method: 'POST', body: formData })
      const payload = await response.json().catch(() => null)

      if (!response.ok) {
        if (response.status === 401) {
          const hasOauthSuccessCallback = oauthStatus === 'success'
          const shouldRetryOauth =
            !hasOauthSuccessCallback && !oauthRetryAttemptedRef.current

          pushTalentPoolGtmEvent('talent_pool_submit_unauthorized', {
            page: 'talent-pool',
            placement: 'submit',
            step,
            is_resume: isResume,
            should_auto_submit: shouldAutoSubmit,
            oauth_status: oauthStatus ?? null,
            oauth_reason: oauthReason ?? null,
            has_file: true,
          })

          if (!shouldRetryOauth) {
            pushTalentPoolGtmEvent('talent_pool_submit_oauth_retry_blocked', {
              page: 'talent-pool',
              placement: 'submit',
              step,
              oauth_status: oauthStatus ?? null,
              oauth_reason: oauthReason ?? null,
              reason: hasOauthSuccessCallback
                ? 'oauth_success_callback'
                : 'oauth_already_attempted',
            })

            if (hasOauthSuccessCallback) {
              pushTalentPoolGtmEvent(
                'talent_pool_submit_unauthorized_after_oauth',
                {
                  page: 'talent-pool',
                  placement: 'submit',
                  step,
                  oauth_reason: oauthReason ?? null,
                },
              )
            }

            setSubmitError(loginSyncErrorMessage)
            setIsSubmitting(false)
            return
          }

          pushTalentPoolGtmEvent('talent_pool_submit_requires_oauth', {
            page: 'talent-pool',
            placement: 'submit',
            oauth_status: oauthStatus ?? null,
          })
          oauthRetryAttemptedRef.current = true
          setIsSubmitting(false)
          await handleGoogleLogin()
          return
        }

        const message =
          typeof payload?.error === 'string'
            ? payload.error
            : 'Terjadi kesalahan. Silakan coba lagi.'
        setSubmitError(message)
        setIsSubmitting(false)
        return
      }

      const cvId = payload?.cvId as string
      markSubmitted(cvId)
      await deleteStoredTalentPoolCV()
      pushTalentPoolGtmEvent('talent_pool_submit_success', {
        page: 'talent-pool',
        cv_id: cvId,
      })
      routerPush('/talent-pool?thank-you')
    } catch {
      setSubmitError('Terjadi kesalahan. Silakan coba lagi.')
      setIsSubmitting(false)
    } finally {
      submitInFlightRef.current = false
    }
  }, [
    copy.validation,
    draft,
    file,
    handleGoogleLogin,
    isResume,
    markSubmitted,
    oauthReason,
    oauthStatus,
    routerPush,
    setFile,
    setStoredFileName,
    setSubmitError,
    shouldAutoSubmit,
    step,
    validatePersonalStep,
    validatePreferencesStep,
  ])

  useEffect(() => {
    if (!isResume || !shouldAutoSubmit || !hasHydrated) return
    if (hasPersistedSubmission) return
    if (autoSubmitAttemptedRef.current || autoSubmitInFlightRef.current) return

    const autoSubmit = async () => {
      autoSubmitInFlightRef.current = true
      try {
        const supabase = createClient()
        const {
          data: { user },
        } = await supabase.auth.getUser()
        if (!user) return
        autoSubmitAttemptedRef.current = true
        await handleSubmit()
      } finally {
        autoSubmitInFlightRef.current = false
      }
    }

    void autoSubmit()
  }, [handleSubmit, hasHydrated, hasPersistedSubmission, isResume, shouldAutoSubmit])

  const isFormStep = step === 'personal' || step === 'preferences' || step === 'submit'
  const stepLabels = [copy.steps.info, copy.steps.preferences, copy.steps.submit]
  const stepIndex =
    step === 'personal' ? 0 : step === 'preferences' ? 1 : step === 'submit' ? 2 : -1

  return {
    step,
    isFormStep,
    stepIndex,
    stepLabels,
    fieldErrors,
    setFieldErrors,
    isSubmitting,
    isAuthenticating,
    goBack,
    goNext,
    handleLandingNext,
    handleSubmit,
  }
}
